import * as React from 'react';
import { useContext } from 'react';
import { useSelector } from '@/state/store';
import {
    stateMap,
    stateOmrader,
    statePreferences,
    stateSearchDropdownState,
    stateUser,
    stateVegnett,
} from '@/selectors/selectors';
import { kartutsnittFromWktStrings, wktToGeometry } from '@/utils/SpatialHelper';
import urlJoin from 'url-join';
import { memoize } from 'lodash-es';
import { OmraderState, VegobjekttypeCategoryState, VegobjekttypeState } from '@/state';
import { download } from '@/utils/Utils';
import {
    ConfigAndData,
    ConfigAndDataContext,
} from '@/state/context/ConfigAndDataContext';
import { getBlob } from '@/server/server';

const useBaseVegnettParams = () => {
    const areaState = useSelector(stateOmrader);
    const areaQueries = areaState.toQueries();
    const mapQueries = useSelector(stateMap).toQueries();
    const roadNetQueries = useSelector(stateVegnett).toQueries();

    const params = {
        ...mapQueries.toParams(),
        ...areaQueries.toParams(),
        ...roadNetQueries
            .withTidspunkt(useSelector(stateSearchDropdownState).timestamp)
            .toParams(),
    };

    return { params, areaState };
};

const useVegnettParamsWithExportPreferences = () => {
    const { params, areaState } = useBaseVegnettParams();
    const preferences = useSelector(statePreferences);

    return {
        params: {
            ...params,
            ...preferences.toExportQueries().toParams(),
        },
        areaState,
    };
};

const buildVegnettQuery = (params: Record<string, string>) => {
    const hasOmradeWithGeometry =
        !!params.fylke ||
        !!params.kommune ||
        !!params.kontraktsomrade ||
        !!params.riksvegrute;

    return (
        '?' +
        Object.keys(params)
            .filter(
                key =>
                    !hasOmradeWithGeometry ||
                    (hasOmradeWithGeometry && key !== 'kartutsnitt')
            )
            .map(key => `${key}=${encodeURIComponent(params[key])}`)
            .join('&')
    );
};

export const useVegnettUrls = (
    restapi_url: string,
    eksport_url: string,
    eksportApiUrl: string | null = restapi_url
) => {
    const { params, areaState } = useVegnettParamsWithExportPreferences();
    const base = `/vegnett/veglenkesekvenser/segmentert`;
    const query = buildVegnettQuery(params);

    const apiParam = eksportApiUrl ? `&api=${encodeURIComponent(eksportApiUrl)}` : '';
    const charsetParam = '&tegnsett=utf-8';
    const sosiutsnittParam = !query.includes('kartutsnitt')
        ? '&sosiutsnitt=' +
          kartutsnittFromWktStrings(
              areaState
                  .withVegsystemreferanser([])
                  .getFullList()
                  .map(omrade => omrade?.vegobjekt?.geometri?.wkt)
                  .filter((wkt): wkt is string => Boolean(wkt))
          ).toQueryString()
        : '';

    return {
        json: urlJoin(restapi_url, base, query),
        sosi: urlJoin(
            eksport_url,
            base + '.sos',
            query + charsetParam + apiParam + sosiutsnittParam
        ),
        csv: urlJoin(eksport_url, base + '.csv', query + apiParam),
    };
};

export const isSensitive = memoize((typeId: number, context: ConfigAndData) => {
    const type = context.data.datakatalog.byId[typeId];
    return type.sensitiv || type.egenskapstyper.some(e => e.sensitivitet > 0);
});

export const useIsSensitiveAvailable = (typeId: number | null): boolean => {
    const context = useContext(ConfigAndDataContext);
    const userState = useSelector(stateUser);
    return typeId !== null && userState.isLoggedIn && isSensitive(typeId, context);
};

const useBaseVegobjektParams = (
    votState: VegobjekttypeState,
    catState?: VegobjekttypeCategoryState
) => {
    const mapState = useSelector(stateMap);
    const omraderState = useSelector(stateOmrader);
    const vegnettState = useSelector(stateVegnett);
    const searchDropdownState = useSelector(stateSearchDropdownState);

    const votQueries = !catState
        ? votState.toQueries().withTidspunkt(searchDropdownState.timestamp)
        : votState
              .toQueries()
              .withTidspunkt(searchDropdownState.timestamp)
              .withKategorifilter(catState.filters);

    const params = {
        ...votQueries.toParams(),
        ...mapState.toQueries().toParams(),
        ...omraderState.toOmradeQueries(undefined).toParams(),
        ...vegnettState.toQueries().toParams(),
    };

    return { params, omraderState };
};

const useVegobjektParamsWithExportPreferences = (
    votState: VegobjekttypeState,
    catState?: VegobjekttypeCategoryState
) => {
    const { params, omraderState } = useBaseVegobjektParams(votState, catState);
    const preferences = useSelector(statePreferences);

    return {
        params: {
            ...params,
            ...preferences.toExportQueries().toParams(),
        },
        omraderState,
    };
};

export const buildAreaParams = (omraderState: OmraderState) => {
    const geometries = omraderState
        .getGeometryFetchables()
        .map(item => item?.vegobjekt?.geometri?.wkt)
        .filter((wkt): wkt is string => Boolean(wkt))
        .map(wkt => wktToGeometry(wkt));

    let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

    geometries.forEach(geometry => {
        const extent = geometry.getExtent();
        minX = Math.min(minX, extent[0]);
        minY = Math.min(minY, extent[1]);
        maxX = Math.max(maxX, extent[2]);
        maxY = Math.max(maxY, extent[3]);
    });

    return minX + ',' + minY + ',' + maxX + ',' + maxY;
};

export const buildUrlParams = (params: Record<string, string>) => {
    return Object.keys(params)
        .reduce(
            (prev: string[], key: string) =>
                prev.concat(`${key}=${encodeURIComponent(params[key])}`),
            []
        )
        .concat(`inkluder=${encodeURIComponent('alle')}`)
        .join('&');
};

export const useUrlsParams = (
    votState: VegobjekttypeState,
    catState?: VegobjekttypeCategoryState
): string => {
    const { params, omraderState } = useVegobjektParamsWithExportPreferences(
        votState,
        catState
    );

    const urlParams =
        !!params.fylke ||
        !!params.kommune ||
        !!params.kontraktsomrade ||
        !!params.riksvegrute
            ? { ...params, kartutsnitt: buildAreaParams(omraderState) }
            : params;

    return buildUrlParams(urlParams);
};

export interface ExportLinkParams {
    typeId: number;
    baseUrlParams: string;
    eksportUrl: string;
    apiUrl?: string;
}

export const buildCsvLink = (params: ExportLinkParams): string => {
    const { typeId, baseUrlParams, eksportUrl, apiUrl } = params;
    const endpoint = `/vegobjekter/${typeId}.csv?`;
    const urlParams = new URLSearchParams(baseUrlParams);
    if (apiUrl) urlParams.append('api', apiUrl);
    return eksportUrl + endpoint + urlParams.toString();
};

export const buildSosiLink = (
    params: ExportLinkParams & {
        exportEnumFormat: string | null;
        omraderState: OmraderState;
    }
): string => {
    const { typeId, baseUrlParams, eksportUrl, apiUrl, exportEnumFormat, omraderState } =
        params;
    const endpoint = `/vegobjekter/${typeId}.sos?`;
    const urlParams = new URLSearchParams(baseUrlParams);
    if (urlParams.has('kartutsnitt') && !omraderState.loading) {
        const sosiUtsnitt = kartutsnittFromWktStrings(
            omraderState
                .withVegsystemreferanser([])
                .getFullList()
                .map(omrade => omrade?.vegobjekt?.geometri?.wkt)
                .filter((wkt): wkt is string => Boolean(wkt))
        ).toQueryString();
        urlParams.append('sosiutsnitt', sosiUtsnitt);
    }
    if (apiUrl) urlParams.append('api', apiUrl);
    urlParams.append('tegnsett', 'utf-8');
    if (exportEnumFormat) urlParams.append('enumFormat', exportEnumFormat);
    return eksportUrl + endpoint + urlParams.toString();
};

export const handleDownload = async (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    isSensitiveAvailable: boolean,
    downloadUrl: string,
    context: ConfigAndData,
    setDownloading: (downloading: boolean) => void
) => {
    e.preventDefault();
    setDownloading(true);

    const match = downloadUrl.match(/vegobjekter\/(\d+)\.(sos|csv)/);
    const vegobjektId = match ? parseInt(match[1]) : undefined;
    const roadObjectTypeName =
        context.data.datakatalog.vegobjekttyper.find(type => type.id === vegobjektId)
            ?.sosinvdbnavn ?? 'vegobjekt';
    const sensitivityLabel = isSensitiveAvailable ? '-sensitiv' : '';
    const fileType = match ? match[2] : 'sos';

    const fileName = `${roadObjectTypeName}${sensitivityLabel}-eksport.${fileType}`;
    const blob = await getBlob(downloadUrl, isSensitiveAvailable);
    const objectURL = URL.createObjectURL(blob);

    download(objectURL, fileName);

    URL.revokeObjectURL(objectURL);
    setDownloading(false);
};
